import { useSelector } from "react-redux";

import { DashboardCard2 } from "@/components/layout/DashboardCard2";
import type { NoteDetail } from "@/core/types";
import type { RootState } from "@/lib/types/state";
import { Spinner2 } from "@/ui2/atoms/Spinner";

import { formatSignatureDate } from "../helpers/formatSignatureDate";
import { isNoteCompleted } from "../helpers/isNoteCompleted";

import { NoteContentRouter } from "./NoteContentRouter";
import { SignatureList } from "./(signature)/SignatureList";

import "./BaseNoteLayout.print.styles.scss";

type BaseNoteLayoutProps = {
  isEditing: boolean;
  note: NoteDetail;
  patientName: string | undefined;
  refetchNote: () => void;
  setIsEditing: (v: boolean) => void;
};

export function BaseNoteLayout({
  isEditing,
  note,
  patientName,
  refetchNote,
  setIsEditing,
}: BaseNoteLayoutProps) {
  const profileName = useSelector<RootState, string | null | undefined>(
    (state) => state.auth.profile?.name,
  );

  if (!note) return <Spinner2 className="tw:mx-auto tw:mt-30" />;

  const isComplete = isNoteCompleted(note.note_state);

  return (
    <DashboardCard2
      as="article"
      className="tw:flex tw:flex-col tw:gap-6"
      classNameHeader="tw:mb-0!"
      rawChildren
      title={note.title}
    >
      <NoteContentRouter
        isEditing={isEditing}
        note={note}
        patientName={patientName}
        profileName={profileName}
        refetchNote={refetchNote}
        setIsEditing={setIsEditing}
      />

      {!isEditing && (
        <SignatureList
          cosignatureTimestamp={note.cosignature_timestamp}
          cosignedBy={isComplete ? note.cosigned_by : undefined}
          credentials={note.provider_cp_credentials}
          documentId={note.id}
          documentType="note"
          isCosignRequired={note.is_cosign_required}
          isNoteComplete={isComplete}
          providerPractice={note.provider_practice}
          signature={note.signature}
          signatureDate={formatSignatureDate(note)}
          signatureTimestamp={note.signature_timestamp}
        />
      )}
    </DashboardCard2>
  );
}
